import { useEffect, useMemo, useState } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import {
  obtenerPqrs,
  PqrRecord,
  responderPqr,
  cerrarPqr,
} from "../../../../api/pqrService";
import Swal from "sweetalert2";
import {
  MessageSquare,
  RefreshCcw,
  Filter,
  AlertCircle,
  Calendar,
  Search,
  Eye,
  ClipboardList,
  CheckCircle,
  Inbox,
} from "lucide-react";

const ALLOWED_ROLES = ["AD", "AS"];

const statusStyles: Record<
  string,
  { text: string; badge: string; pill: string }
> = {
  A: {
    text: "Abierto",
    badge: "text-orange-600 bg-orange-50 border border-orange-200",
    pill: "bg-orange-100 text-orange-700",
  },
  C: {
    text: "Cerrado",
    badge: "text-green-600 bg-green-50 border border-green-200",
    pill: "bg-green-100 text-green-700",
  },
  R: {
    text: "Respondido",
    badge: "text-blue-600 bg-blue-50 border border-blue-200",
    pill: "bg-blue-100 text-blue-700",
  },
};

const getStatusStyle = (estatus: string) => {
  const key = (estatus || "").trim().charAt(0).toUpperCase();
  return (
    statusStyles[key] || {
      text: estatus || "Sin estado",
      badge: "text-gray-600 bg-gray-50 border border-gray-200",
      pill: "bg-gray-100 text-gray-700",
    }
  );
};

const formatDate = (value: string) => {
  if (!value) return "Sin fecha";
  return value;
};

const getInitialFilters = () => ({
  codigo: "",
  apartamentoCodigo: "",
  tipoCodigo: "",
  estatusCodigo: "",
  fechaInicial: "",
  fechaFinal: "",
});

type FiltersState = ReturnType<typeof getInitialFilters>;

export default function PqrListPage() {
  const { session } = useAuth();
  const unidadCodigo = session?.unidad?.id || "";
  const rolCodigo = (session?.rol?.id || "").toUpperCase();
  const isAuthorized = ALLOWED_ROLES.includes(rolCodigo);

  const [isLoading, setIsLoading] = useState(false);
  const [pqrs, setPqrs] = useState<PqrRecord[]>([]);
  const [filters, setFilters] = useState<FiltersState>(getInitialFilters());
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPqr, setSelectedPqr] = useState<PqrRecord | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [responseValue, setResponseValue] = useState("");
  const [actionLoading, setActionLoading] = useState<"save" | "close" | null>(
    null
  );

  useEffect(() => {
    if (isAuthorized && unidadCodigo) {
      fetchPqrs(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, unidadCodigo]);

  const fetchPqrs = async (silent = false, customFilters?: FiltersState) => {
    if (!unidadCodigo) {
      return;
    }

    if (!silent) {
      setIsLoading(true);
    }

    try {
      const activeFilters = customFilters ?? filters;
      const response = await obtenerPqrs({
        codigo: activeFilters.codigo.trim(),
        "unidad residencial codigo": unidadCodigo,
        "apartamento codigo": activeFilters.apartamentoCodigo.trim(),
        "tipo codigo": activeFilters.tipoCodigo.trim(),
        "estatus codigo": activeFilters.estatusCodigo.trim(),
        "fecha inicial": activeFilters.fechaInicial,
        "fecha final": activeFilters.fechaFinal,
      });

      if (response.code === "1" && Array.isArray(response.object_)) {
        setPqrs(response.object_);
        setSelectedPqr((prev) => {
          if (!prev) return null;

          const stillExists = response.object_.find(
            (item) => item.pqr_codigo === prev.pqr_codigo
          );
          return stillExists || null;
        });
      } else {
        setPqrs([]);
        setSelectedPqr(null);
      }

      setLastUpdated(new Date());
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No se pudieron cargar los PQR";

      Swal.fire({
        icon: "error",
        title: "No se pudo cargar la información",
        text: message,
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field: keyof FiltersState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    const reset = getInitialFilters();
    setFilters(reset);
    setSearchTerm("");
    setSelectedPqr(null);
    fetchPqrs(false, reset);
  };

  useEffect(() => {
    if (selectedPqr) {
      setResponseValue(selectedPqr.pqr_respuesta || "");
    } else {
      setResponseValue("");
    }
  }, [selectedPqr]);

  const filteredPqrs = useMemo(() => {
    if (!searchTerm.trim()) return pqrs;

    const term = searchTerm.toLowerCase();
    return pqrs.filter((item) => {
      const valuesToCheck = [
        item.pqr_codigo?.toString(),
        item.pqr_apartamento_nombre,
        item.pqr_tipo_nombre,
        item.pqr_asunto,
        item.pqr_descripcion,
        item.pqr_estatus_nombre,
      ];

      return valuesToCheck.some((value) =>
        (value || "").toLowerCase().includes(term)
      );
    });
  }, [pqrs, searchTerm]);

  const summary = useMemo(() => {
    const abiertos = pqrs.filter((item) =>
      (item.pqr_estatus_nombre || "")
        .toLowerCase()
        .includes("abiert")
    ).length;

    const cerrados = pqrs.filter((item) =>
      (item.pqr_estatus_nombre || "")
        .toLowerCase()
        .includes("cerrad")
    ).length;

    const respondidos = pqrs.filter(
      (item) => item.pqr_respuesta && item.pqr_respuesta.trim().length > 0
    ).length;

    return {
      total: pqrs.length,
      abiertos,
      cerrados,
      respondidos,
    };
  }, [pqrs]);

  const handleSaveResponse = async () => {
    if (!selectedPqr) {
      return;
    }

    const trimmed = responseValue.trim();

    if (!trimmed) {
      await Swal.fire({
        icon: "info",
        title: "Respuesta requerida",
        text: "Por favor digita una respuesta antes de guardar.",
        confirmButtonColor: "#3d5a40",
      });
      return;
    }

    try {
      setActionLoading("save");
      await responderPqr({
        codigo: selectedPqr.pqr_codigo.toString(),
        respuesta: trimmed,
      });

      setSelectedPqr((prev) =>
        prev
          ? {
              ...prev,
              pqr_respuesta: trimmed,
              pqr_estatus_nombre: "En proceso",
              pqr_estatus: "P",
            }
          : prev
      );
      setPqrs((prev) =>
        prev.map((item) =>
          item.pqr_codigo === selectedPqr.pqr_codigo
            ? {
                ...item,
                pqr_respuesta: trimmed,
                pqr_estatus_nombre: "En proceso",
                pqr_estatus: "P",
              }
            : item
        )
      );
      await fetchPqrs(true);

      await Swal.fire({
        icon: "success",
        title: "Respuesta registrada",
        text: "El PQR pasó a estatus En proceso.",
        confirmButtonColor: "#3d5a40",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No fue posible guardar la respuesta";
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: message,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClosePqr = async () => {
    if (!selectedPqr) {
      return;
    }

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "¿Cerrar este PQR?",
      text: "Esta acción marcará el caso como resuelto.",
      showCancelButton: true,
      confirmButtonText: "Sí, cerrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6b7280",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      setActionLoading("close");
      await cerrarPqr({
        codigo: selectedPqr.pqr_codigo.toString(),
      });

      const closureDate =
        selectedPqr.pqr_fecha_cierre || new Date().toLocaleString();
      setSelectedPqr((prev) =>
        prev
          ? {
              ...prev,
              pqr_estatus_nombre: "Cerrado",
              pqr_estatus: "C",
              pqr_fecha_cierre: closureDate,
            }
          : prev
      );
      setPqrs((prev) =>
        prev.map((item) =>
          item.pqr_codigo === selectedPqr.pqr_codigo
            ? {
                ...item,
                pqr_estatus_nombre: "Cerrado",
                pqr_estatus: "C",
                pqr_fecha_cierre: closureDate,
              }
            : item
        )
      );
      await fetchPqrs(true);

      await Swal.fire({
        icon: "success",
        title: "PQR cerrado",
        text: "El caso fue marcado como solucionado.",
        confirmButtonColor: "#3d5a40",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No fue posible cerrar el PQR";
      await Swal.fire({
        icon: "error",
        title: "Error al cerrar",
        text: message,
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 md:ml-64 flex items-center justify-center">
          <Card className="max-w-lg shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle size={24} />
                Acceso restringido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Esta sección está disponible únicamente para los roles
                Administrador (AD) y AdminSocio (AS). Si necesitas acceso,
                contacta al equipo de EasyPH.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all space-y-4 sm:space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-military-600">
            Reporte de PQR
          </h1>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {lastUpdated && (
              <span className="text-xs sm:text-sm text-gray-500">
                Actualizado: {lastUpdated.toLocaleString()}
              </span>
            )}
            <Button
              variant="outline"
              className="gap-2 w-full sm:w-auto"
              onClick={() => fetchPqrs()}
              disabled={isLoading}
            >
              <RefreshCcw
                size={18}
                className={isLoading ? "animate-spin" : ""}
              />
              Actualizar
            </Button>
          </div>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-military-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total PQR</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {summary.total}
                  </p>
                </div>
                <div className="bg-military-100 p-3 rounded-full">
                  <ClipboardList size={24} className="text-military-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Abiertos</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {summary.abiertos}
                  </p>
                </div>
                <div className="bg-orange-100 p-3 rounded-full">
                  <AlertCircle size={24} className="text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-blue-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Respondidos</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {summary.respondidos}
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <Inbox size={24} className="text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Cerrados</p>
                  <p className="text-3xl font-bold text-green-600">
                    {summary.cerrados}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <CheckCircle size={24} className="text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Card className="shadow-lg">
          <CardHeader className="flex flex-col gap-2 border-b">
            <div className="flex items-center gap-2 text-military-600">
              <Filter size={20} />
              <CardTitle>Filtros avanzados</CardTitle>
            </div>
            <p className="text-sm text-gray-500">
              Usa estos filtros para consultar PQR específicos y refina la
              búsqueda en tiempo real con el cuadro de texto.
            </p>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Código PQR
                </label>
                <Input
                  placeholder="Ej: 008"
                  value={filters.codigo}
                  onChange={(e) => handleFilterChange("codigo", e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar size={16} />
                  Fecha inicial
                </label>
                <Input
                  type="date"
                  value={filters.fechaInicial}
                  onChange={(e) =>
                    handleFilterChange("fechaInicial", e.target.value)
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar size={16} />
                  Fecha final
                </label>
                <Input
                  type="date"
                  value={filters.fechaFinal}
                  onChange={(e) =>
                    handleFilterChange("fechaFinal", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2 flex-1">
                <Search size={18} className="text-gray-500" />
                <Input
                  placeholder="Filtrar por asunto, descripción, apartamento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  className="bg-military-500 hover:bg-military-600 text-white"
                  onClick={() => fetchPqrs()}
                  disabled={isLoading}
                >
                  Consultar
                </Button>
                <Button variant="outline" onClick={handleClearFilters}>
                  Limpiar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          <Card className="xl:col-span-2 shadow-lg">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare size={20} />
                Lista de PQR ({filteredPqrs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
                  <p className="text-gray-600">Cargando PQR...</p>
                </div>
              ) : filteredPqrs.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare
                    size={48}
                    className="mx-auto text-gray-400 mb-4"
                  />
                  <p className="text-gray-600 font-medium">
                    No se encontraron registros
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Ajusta los filtros o actualiza la información para ver
                    nuevos PQR.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {filteredPqrs.map((item) => {
                    const style = getStatusStyle(item.pqr_estatus);
                    const isSelected =
                      selectedPqr?.pqr_codigo === item.pqr_codigo;
                    return (
                      <button
                        key={item.pqr_codigo}
                        onClick={() => setSelectedPqr(item)}
                        className={`w-full text-left rounded-2xl border bg-white transition shadow-sm hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-military-500 ${
                          isSelected ? "ring-2 ring-military-400" : ""
                        }`}
                      >
                        <div className="flex gap-4 p-4">
                          <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center border">
                            {item.pqr_ruta_foto ? (
                              <img
                                src={item.pqr_ruta_foto}
                                alt={item.pqr_asunto}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.currentTarget;
                                  target.style.display = "none";
                                }}
                              />
                            ) : (
                              <MessageSquare
                                size={32}
                                className="text-gray-400"
                              />
                            )}
                          </div>
                          <div className="flex-1 flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-500">
                                #{item.pqr_codigo.toString().padStart(4, "0")}
                              </span>
                              <span
                                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${style.badge}`}
                              >
                                {item.pqr_estatus_nombre || style.text}
                              </span>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {item.pqr_asunto}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Tipo:{" "}
                              <span className="font-semibold text-gray-800">
                                {item.pqr_tipo_nombre}
                              </span>
                            </p>
                            <p className="text-sm text-gray-600">
                              Apartamento:{" "}
                              <span className="font-semibold text-gray-800">
                                {item.pqr_apartamento_nombre || "N/D"}
                              </span>
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <Eye size={20} />
                Detalle del PQR
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {!selectedPqr ? (
                <div className="text-center py-12">
                  <Eye size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-600">
                    Selecciona un registro para ver su detalle completo.
                  </p>
                </div>
              ) : (
                <>
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div
                      className="w-full rounded-lg bg-white border border-dashed border-gray-300 flex items-center justify-center overflow-hidden"
                      style={{ minHeight: "200px" }}
                    >
                      {selectedPqr.pqr_ruta_foto ? (
                        <img
                          src={selectedPqr.pqr_ruta_foto}
                          alt={`Evidencia PQR ${selectedPqr.pqr_codigo}`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="text-center text-gray-400 flex flex-col items-center gap-2 py-8">
                          <MessageSquare size={36} />
                          <span>Sin imagen adjunta</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs uppercase text-gray-500 font-semibold">
                      Código
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      #{selectedPqr.pqr_codigo.toString().padStart(4, "0")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">
                        Apartamento
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        {selectedPqr.pqr_apartamento_nombre}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedPqr.pqr_apartamento}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">
                        Tipo
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        {selectedPqr.pqr_tipo_nombre}
                      </p>
                      <span
                        className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
                          selectedPqr.pqr_estatus
                        ).pill}`}
                      >
                        {selectedPqr.pqr_estatus_nombre}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">
                        Fecha creación
                      </p>
                      <p className="text-sm text-gray-700">
                        {formatDate(selectedPqr.pqr_fecha_creacion)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-gray-500 font-semibold">
                        Fecha cierre
                      </p>
                      <p className="text-sm text-gray-700">
                        {selectedPqr.pqr_fecha_cierre || "Sin cerrar"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase text-gray-500 font-semibold mb-1">
                      Asunto
                    </p>
                    <p className="text-base font-semibold text-gray-900">
                      {selectedPqr.pqr_asunto}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase text-gray-500 font-semibold mb-1">
                      Descripción
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 border border-gray-200 min-h-[90px]">
                      {selectedPqr.pqr_descripcion || "Sin descripción"}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase text-gray-500 font-semibold mb-2">
                      Respuesta registrada
                    </p>
                    <Textarea
                      value={selectedPqr.pqr_respuesta || ""}
                      readOnly
                      placeholder="Sin respuesta registrada"
                      className="min-h-[120px]"
                    />
                  </div>

                  {selectedPqr.pqr_ruta_foto && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        window.open(selectedPqr.pqr_ruta_foto, "_blank")
                      }
                      className="gap-2"
                    >
                      <Eye size={16} />
                      Ver imagen en otra ventana
                    </Button>
                  )}

                  {(() => {
                    const isCerrado = 
                      selectedPqr.pqr_estatus?.toUpperCase() === "C" ||
                      (selectedPqr.pqr_estatus_nombre || "")
                        .toLowerCase()
                        .includes("cerrad");
                    
                    if (isCerrado) {
                      return (
                        <div className="border-t border-gray-200 pt-4">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-green-700">
                              <CheckCircle size={20} />
                              <p className="font-semibold">Este PQR está cerrado</p>
                            </div>
                            <p className="text-sm text-green-600 mt-2">
                              No se pueden realizar más acciones sobre este PQR.
                            </p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="border-t border-gray-200 pt-4 space-y-3">
                        <p className="text-xs uppercase text-gray-500 font-semibold">
                          Acciones sobre el PQR
                        </p>
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col gap-2">
                            <Textarea
                              value={responseValue}
                              onChange={(e) => setResponseValue(e.target.value)}
                              placeholder="Escribe aquí la respuesta que se notificará al residente..."
                              className="min-h-[140px]"
                            />
                            <p className="text-xs text-gray-500">
                              Al guardar, el PQR cambiará al estado <strong>En proceso</strong>.
                            </p>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <Button
                              className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                              onClick={handleSaveResponse}
                              disabled={actionLoading === "save"}
                            >
                              {actionLoading === "save" ? "Guardando..." : "Guardar respuesta"}
                            </Button>
                            <Button
                              variant="destructive"
                              className="flex-1"
                              onClick={handleClosePqr}
                              disabled={actionLoading === "close"}
                            >
                              {actionLoading === "close" ? "Cerrando..." : "Cerrar PQR"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

